import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  NotificationService,
  NOTIFICATION_TYPE_INFO,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_ERROR,
} from 'ui-components/services/notification-service';

import { SellerSettings } from '../../enums/seller-settings/seller-settings';
import { useSellerSetting } from '../../hooks/use-seller-setting';
import Global from '../../store/global-selectors';
import * as Actions from '../../store/inventory/inventory.actions';
import { allocateFulfillmentPlansPromise } from '../../store/inventory/inventory.api';
import {
  ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS,
  SOME_FULFILLMENT_PLANS_ALLOCATED_STATUS,
  NO_FULFILLMENT_PLANS_ALLOCATED_STATUS,
  INSUFFICIENT_PERMISSIONS_STATUS,
  ALLOCATION_CHECK_FAILED,
} from '../../store/inventory/inventory.constants';
import { messages } from '../../store/inventory/messages';
import { silentRequestGetOrders } from '../../store/orders/orders.actions';
import { SellerProperties } from '../../store/seller/seller-properties';
import { useSubscribableState, IS_ALLOCATING_INVENTORY } from '../../subscribable-state';

const ALLOW = 'allow';

export const useInventoryAllocation = () => {
  const dispatch = useDispatch();
  const shipIfNoStockSellerSetting = useSellerSetting(SellerSettings.InventoryShipNoStock);
  const { isInventoryEnabled } = useSelector((state) => new SellerProperties({ state }));
  const selectedFulfillmentPlanIds = useSelector(Global.selectSelectedOrdersIds);
  const fulfillmentItems = useSelector((state) => {
    return Global.selectEntitiesFulfillmentPlansItemsByFulfillmentPlanIds(state, { fulfillmentPlanIds: selectedFulfillmentPlanIds });
  });

  const [isAllocating, setIsAllocating] = useSubscribableState(IS_ALLOCATING_INVENTORY);

  const allocateInventory = useCallback(
    (fulfillmentPlanIds, successAction, allowInventoryCheck = true, shouldRetryOnFailure = false) => {
      if (!isInventoryEnabled) {
        if (successAction) {
          dispatch(successAction);
        }
        return;
      }
      setIsAllocating(true);
      const notificationId = NotificationService.showNotification({
        title: <FormattedMessage {...messages.allocationStartingTitle} />,
        content: <FormattedMessage {...messages.allocationStartingMessage} />,
        notificationType: NOTIFICATION_TYPE_INFO,
      });
      allocateFulfillmentPlansPromise(fulfillmentPlanIds, undefined, allowInventoryCheck)
        .then((data) => {
          setIsAllocating(false);
          const { status, inventoryAllocationCheckInfo, inventoryAllocationResults } = data;
          const checkFailed = inventoryAllocationCheckInfo?.status === ALLOCATION_CHECK_FAILED;
          const hasInsufficientPermissions = status === INSUFFICIENT_PERMISSIONS_STATUS;
          const results = inventoryAllocationCheckInfo?.results;
          const items = fulfillmentItems.map((item) => {
            const allocationItem = results?.find((result) => result.fulfillmentPlanItemId.toString() === item.fulfillmentPlanItemId);
            return {
              ...allocationItem,
              ...item,
            };
          });

          // Handle insufficient permissions error
          if (hasInsufficientPermissions) {
            NotificationService.updateNotification(notificationId, {
              title: <FormattedMessage {...messages.allocationPermissionsErrorTitle} />,
              content: <FormattedMessage {...messages.allocationPermissionsErrorMessage} />,
              notificationType: NOTIFICATION_TYPE_ERROR,
            });
            return;
          }

          // This check is required because at the moment, an individual item will fail to allocate
          // but the backend still sends a successful allocation for the whole operation
          // Here's a ticket for the behavior: https://auctane.atlassian.net/browse/SPD-7773
          // only warn if the seller cares about the failure - SPD-8168
          if (checkFailed && shipIfNoStockSellerSetting !== ALLOW && allowInventoryCheck) {
            // When allocation fails and we're checking inventory, show the modal
            const allocationFailure = Actions.allocateFulfillmentPlansFailure({
              inventoryAllocationCheckInfo,
              items,
              // Only set up retry if shouldRetryOnFailure is true (Orders Grid flow)
              // Otherwise, pass the successAction (Create Label flow)
              ...(shouldRetryOnFailure
                ? {
                    onSuccess: () => {
                      // Retry allocation without inventory check
                      allocateInventory(fulfillmentPlanIds, successAction, false, false);
                    },
                  }
                : {
                    successAction,
                  }),
            });
            dispatch(allocationFailure);
            return;
          }

          const isNoAllocationNeeded = !checkFailed && status === NO_FULFILLMENT_PLANS_ALLOCATED_STATUS;
          const isSomeAllocated = !checkFailed && status === SOME_FULFILLMENT_PLANS_ALLOCATED_STATUS;
          const isSuccessCase =
            !checkFailed && (isNoAllocationNeeded || isSomeAllocated || status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS);

          if (status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS && !checkFailed) {
            // Full success - all items allocated without issues
            NotificationService.updateNotification(notificationId, {
              title: <FormattedMessage {...messages.allocationSuccessTitle} />,
              content: <FormattedMessage {...messages.allocationSuccessMessage} />,
              notificationType: NOTIFICATION_TYPE_SUCCESS,
            });
          } else if (isSomeAllocated) {
            // Partial allocation - some items allocated, others didn't need allocation
            NotificationService.updateNotification(notificationId, {
              title: <FormattedMessage {...messages.allocationSomeSuccessTitle} />,
              content: <FormattedMessage {...messages.allocationSomeSuccessMessage} />,
              notificationType: NOTIFICATION_TYPE_SUCCESS,
            });
          } else if (isNoAllocationNeeded) {
            // No items needed allocation (e.g., no inventory tracking for these items)
            NotificationService.updateNotification(notificationId, {
              title: <FormattedMessage {...messages.allocationNoItemsTitle} />,
              content: <FormattedMessage {...messages.allocationNoItemsMessage} />,
              notificationType: NOTIFICATION_TYPE_SUCCESS,
            });
          } else if (!allowInventoryCheck && (status !== ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS || checkFailed)) {
            // Partial success - proceeded despite inventory issues
            NotificationService.updateNotification(notificationId, {
              title: <FormattedMessage {...messages.allocationPartialSuccessTitle} />,
              content: <FormattedMessage {...messages.allocationPartialSuccessMessage} />,
              notificationType: NOTIFICATION_TYPE_SUCCESS,
            });
          }

          if (
            status === ALL_FULFILLMENT_PLANS_ALLOCATED_STATUS ||
            shipIfNoStockSellerSetting === ALLOW ||
            !allowInventoryCheck ||
            isSuccessCase
          ) {
            dispatch(silentRequestGetOrders());
            dispatch(Actions.allocateFulfillmentPlansSuccess(inventoryAllocationResults));
            if (successAction) {
              dispatch(successAction);
            }
            return;
          }

          // This shouldn't happen, but if it does, dispatch the failure without retry
          const allocationFailure = Actions.allocateFulfillmentPlansFailure({
            inventoryAllocationCheckInfo,
            items,
            successAction,
          });
          dispatch(allocationFailure);
        })
        .catch(() => {
          setIsAllocating(false);
        });
    },
    [dispatch, shipIfNoStockSellerSetting, isInventoryEnabled, fulfillmentItems, setIsAllocating],
  );
  return { allocateInventory, isAllocating };
};
